import { LitElement, html } from 'lit';
import { customElement } from 'lit/decorators.js';
import { prodTestThumbSandboxStyles } from '../design/prod-test-thumb-sandbox.styles';
// Side-effect import to register ptc-editor custom element
import '@agwa/product-test-config-builder/components';
import { createEmptyGrid } from '@agwa/product-test-config-builder';
import type {
  ShapeEditor,
  AssetManifest,
} from '@agwa/product-test-config-builder';

/**
 * Product Test Thumbnail Sandbox
 *
 * In-app version of the @agwa/product-test-config-builder lab.
 * Embeds the full ptc-editor component which includes its own
 * preview, properties, structure, and asset panels with scrolling.
 *
 * Route: /experimental/prod-test-thumb-generator
 * Security: Experimental.Access role (AGWA staff only)
 */
@customElement('prod-test-thumb-sandbox')
export class ProdTestThumbSandbox extends LitElement {
  static styles = prodTestThumbSandboxStyles;

  protected firstUpdated(): void {
    void this.initEditor();
  }

  private async initEditor(): Promise<void> {
    await this.updateComplete;
    this.editor.grid = createEmptyGrid('New Composition', 1000, 1000, {
      openingStyle: {
        strokeColor: '#374151',
        strokeWidth: 2,
        fillColor: '#dbeafe',
        fillOpacity: 0.4,
      },
    });
    await this.loadAssetManifest();
  }

  private get editor(): ShapeEditor {
    return this.renderRoot.querySelector('ptc-editor') as ShapeEditor;
  }

  private async loadAssetManifest(): Promise<void> {
    try {
      const response = await fetch('/svg/assetmanifest.json');
      if (!response.ok) {
        console.error(`Failed to load asset manifest: ${response.statusText}`);
        return;
      }
      const manifest = (await response.json()) as AssetManifest;
      this.editor.assetManifest = manifest;
    } catch (e) {
      console.error('Failed to load asset manifest:', e);
    }
  }

  render() {
    return html`
      <div class="editor-container">
        <ptc-editor show-labels></ptc-editor>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'prod-test-thumb-sandbox': ProdTestThumbSandbox;
  }
}
